import React from 'react';
import { Button, Heading, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { WIX_EDITOR_URL } from 'config/consts';
import { biEvents } from 'api/bi-events';
import { BI_BUTTON_NAME, BI_STEP_NAME } from 'config/bi';
import { StepId } from 'config/steps';
import { buildStoresCatalogDashboardUrl, redirects } from 'api/redirects';
import { useAppStore } from 'stores/appStore';
import type { ExistingSiteStoreReadyBoxId } from '../existingSiteStoreReadyUiStore';
import {
  buildYoutubeEmbedSrc,
  EXISTING_SITE_STORE_READY_VIDEO_DELIVERY_ID,
  EXISTING_SITE_STORE_READY_VIDEO_MARGIN_ID,
} from '../existingSiteStoreReadyVideos';
import { EmbeddedVideoPlayer } from './EmbeddedVideoPlayer';
import { ExistingSiteStoreReadyMockupsPanel } from './ExistingSiteStoreReadyMockupsPanel';
import { PanelIconPlaceholder } from './PanelIconPlaceholder';
import styles from '../ExistingSiteStoreReadyStep.scss';

function getEditorHref(metaSiteId: string | undefined): string | undefined {
  return metaSiteId ? `${WIX_EDITOR_URL}/${metaSiteId}` : undefined;
}

function getPublishPathLabel(siteUrl: string | undefined, fallbackLabel: string): string {
  if (siteUrl) {
    try {
      const host = new URL(siteUrl).hostname.replace(/^www\./, '');
      return `${host}/category/all-products`;
    } catch {
      // fall through
    }
  }

  return fallbackLabel;
}

interface ExistingSiteStoreReadyDetailPanelProps {
  boxId: ExistingSiteStoreReadyBoxId;
}

export function ExistingSiteStoreReadyDetailPanel({ boxId }: ExistingSiteStoreReadyDetailPanelProps) {
  const { t } = useTranslation();
  const metaSiteId = useAppStore((s) => s.metaSiteId);
  const siteUrl = useAppStore((s) => s.siteUrl);
  const editorHref = getEditorHref(metaSiteId);
  const publishPathLabel = getPublishPathLabel(siteUrl, t('existing-site-store-ready.path-badge.fallback-label'));
  const storesCatalogHref = metaSiteId ? buildStoresCatalogDashboardUrl(metaSiteId) : undefined;

  const logEditorClick = () => {
    biEvents.actionClicked({
      screenName: BI_STEP_NAME[StepId.StoreReady],
      button_name: BI_BUTTON_NAME.DESIGN_SITE,
    });
  };

  const logPrintfulClick = () => {
    biEvents.actionClicked({
      screenName: BI_STEP_NAME[StepId.StoreReady],
      button_name: BI_BUTTON_NAME.PRINT_ON_DEMAND,
    });
  };

  const logSetMarginClick = () => {
    biEvents.actionClicked({
      screenName: BI_STEP_NAME[StepId.StoreReady],
      button_name: BI_BUTTON_NAME.SET_MY_MARGIN,
    });
  };

  if (boxId === 'page-ready') {
    return (
      <div className={styles.existingSiteStoreReadyPanel}>
        <PanelIconPlaceholder />
        <div className={styles.existingSiteStoreReadyPanelLead}>
          <div className={styles.existingSiteStoreReadyPanelLeadStack}>
            <Heading as="h2" size="medium">
              {t('existing-site-store-ready.panel.page-ready.heading')}
            </Heading>
            <Text size="small" secondary>
              {t('existing-site-store-ready.panel.page-ready.body')}
            </Text>
          </div>
        </div>
        {editorHref ? (
          <Button
            as="a"
            href={editorHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={logEditorClick}
            size="medium"
            priority="secondary"
            className={styles.existingSiteStoreReadyPublishCta}
          >
            {publishPathLabel}
          </Button>
        ) : (
          <div className={styles.existingSiteStoreReadyPathBadgeWrap}>
            <Text size="small" secondary>
              {publishPathLabel}
            </Text>
          </div>
        )}
        <ExistingSiteStoreReadyMockupsPanel gridVariant="two-column" />
      </div>
    );
  }

  if (boxId === 'margin') {
    return (
      <div className={styles.existingSiteStoreReadyPanel}>
        <PanelIconPlaceholder />
        <div className={styles.existingSiteStoreReadyPanelLead}>
          <div className={styles.existingSiteStoreReadyPanelLeadStack}>
            <Heading as="h2" size="medium">
              {t('existing-site-store-ready.panel.margin.heading')}
            </Heading>
            <Text size="small" secondary>
              {t('existing-site-store-ready.panel.margin.body')}
            </Text>
          </div>
        </div>
        {storesCatalogHref ? (
          <Button
            as="a"
            href={storesCatalogHref}
            target="_blank"
            rel="noopener noreferrer"
            size="medium"
            skin="premium"
            onClick={logSetMarginClick}
          >
            {t('existing-site-store-ready.panel.margin.set-margin-cta')}
          </Button>
        ) : null}
        <EmbeddedVideoPlayer
          src={buildYoutubeEmbedSrc(EXISTING_SITE_STORE_READY_VIDEO_MARGIN_ID)}
          title={t('existing-site-store-ready.panel.margin.video-title')}
        />
      </div>
    );
  }

  if (boxId === 'delivery') {
    return (
      <div className={styles.existingSiteStoreReadyPanel}>
        <PanelIconPlaceholder />
        <div className={styles.existingSiteStoreReadyPanelLead}>
          <div className={styles.existingSiteStoreReadyPanelLeadStack}>
            <Heading as="h2" size="medium">
              {t('existing-site-store-ready.panel.delivery.heading')}
            </Heading>
            <Text size="small" secondary>
              {t('existing-site-store-ready.panel.delivery.body')}
            </Text>
          </div>
        </div>
        <Button
          size="medium"
          skin="premium"
          onClick={() => {
            logPrintfulClick();
            void redirects.redirectToPrintOnDemand();
          }}
        >
          {t('existing-site-store-ready.panel.delivery.add-more-products-cta')}
        </Button>
        <EmbeddedVideoPlayer
          src={buildYoutubeEmbedSrc(EXISTING_SITE_STORE_READY_VIDEO_DELIVERY_ID)}
          title={t('existing-site-store-ready.panel.delivery.video-title')}
        />
      </div>
    );
  }

  return (
    <div className={styles.existingSiteStoreReadyPanel}>
      <PanelIconPlaceholder />
      <div className={styles.existingSiteStoreReadyPanelLead}>
        <div className={styles.existingSiteStoreReadyPanelLeadStack}>
          <Heading as="h2" size="medium">
            {t('existing-site-store-ready.panel.add-more-products.heading')}
          </Heading>
          <Text size="small" secondary>
            {t('existing-site-store-ready.panel.add-more-products.body')}
          </Text>
        </div>
      </div>
      <Button
        size="medium"
        skin="premium"
        onClick={() => {
          logPrintfulClick();
          void redirects.redirectToPrintOnDemand();
        }}
      >
        {t('existing-site-store-ready.panel.add-more-products.browse-products')}
      </Button>
      <EmbeddedVideoPlayer
        src={buildYoutubeEmbedSrc(EXISTING_SITE_STORE_READY_VIDEO_MARGIN_ID)}
        title={t('existing-site-store-ready.panel.add-more-products.video-title')}
      />
    </div>
  );
}
